import * as i0 from '@angular/core';
import { Injectable, EventEmitter, Component, ViewChild, Input, Output, NgModule } from '@angular/core';
import 'amazon-connect-streams';
import { BehaviorSubject, Subject, of, filter as filter$1 } from 'rxjs';
import { shareReplay, take, tap, filter, map, switchMap, catchError } from 'rxjs/operators';
import { fromFetch } from 'rxjs/fetch';

class NotificationManager {
    static requestPermission() {
        // @ts-expect-error: declaration file missing types
        const notificationManager = connect.core.getNotificationManager();
        notificationManager.requestPermission();
    }
    static showNotification(options) {
        // @ts-expect-error: declaration file missing types
        const notificationManager = connect.core.getNotificationManager();
        connect.contact((contact) => {
            contact.onConnecting(() => {
                if (contact.isInbound()) {
                    const title = options.notificationTitle ?? 'Incoming call from phone';
                    const message = options.notificationMessage ?? 'Open Custom CCP';
                    const incomingPhoneNumber = contact
                        .getInitialConnection()
                        .getAddress();
                    // @ts-expect-error: declaration file missing types
                    connect.ifMaster('NOTIFICATIONS', () => {
                        notificationManager.show(`${title}: ${incomingPhoneNumber.phoneNumber}`, {
                            body: message,
                            clicked: () => {
                                window.focus();
                            },
                        });
                    });
                }
            });
        });
    }
}

class ConnectProxy {
    constructor(connectObject$) {
        this.connectObject$ = connectObject$.pipe(shareReplay());
    }
    fromData(methodName, args = []) {
        const result$ = new BehaviorSubject(null);
        this.connectObject$.pipe(take(1), tap(connectObject => result$.next(connectObject[methodName](...args)))).subscribe();
        return result$.asObservable().pipe(filter(result => result !== null));
    }
    fromResult(methodName, args = []) {
        const result$ = new Subject();
        this.connectObject$.pipe(take(1), tap(connectObject => {
            connectObject[methodName](...args, {
                success: (data) => result$.next(data),
                failure: (error) => result$.error(error)
            });
        })).subscribe();
        return result$.asObservable();
    }
    fromCallback(methodName) {
        const result$ = new Subject();
        this.connectObject$.pipe(take(1), tap(connectObject => connectObject[methodName]((args) => result$.next(args)))).subscribe();
        return result$.asObservable();
    }
    setData(methodName, args = []) {
        const result$ = new BehaviorSubject(null);
        this.connectObject$.pipe(take(1), tap(connectObject => {
            connectObject[methodName](...args, {
                success: () => result$.next({ success: true }),
                failure: (error) => result$.next({ success: false, error: JSON.parse(error) })
            });
        })).subscribe();
        return result$.asObservable().pipe(filter(result => result !== null));
    }
}

class ConnectionProxy extends ConnectProxy {
    constructor(connection) {
        super(of(connection));
        this.contactId = connection.contactId;
        this.connectionId = connection.connectionId;
    }
    getContactId() {
        return super.fromData('getContactId');
    }
    getConnectionId() {
        return super.fromData('getConnectionId');
    }
    getEndpoint() {
        return super.fromData('getEndpoint');
    }
    getAddress() {
        return super.fromData('getAddress');
    }
    getState() {
        return super.fromData('getState');
    }
    getStateDuration() {
        return super.fromData('getStateDuration');
    }
    getType() {
        return super.fromData('getType');
    }
    getMonitorInfo() {
        return super.fromData('getMonitorInfo');
    }
    isInitialConnection() {
        return super.fromData('isInitialConnection');
    }
    isActive() {
        return super.fromData('isActive');
    }
    isConnected() {
        return super.fromData('isConnected');
    }
    isConnecting() {
        return super.fromData('isConnecting');
    }
    isOnHold() {
        return super.fromData('isOnHold');
    }
    destroy() {
        return super.fromResult('destroy');
    }
    sendDigits(digits) {
        return super.fromResult('sendDigits', [digits]);
    }
    hold() {
        return super.fromResult('hold');
    }
    resume() {
        return super.fromResult('resume');
    }
}

class ContactProxy extends ConnectProxy {
    constructor(contact) {
        super(of(contact));
        this.contactId = contact.contactId;
    }
    onRefresh() {
        return super
            .fromCallback('onRefresh')
            .pipe(map((c) => new ContactProxy(c)));
    }
    onIncoming() {
        return super
            .fromCallback('onIncoming')
            .pipe(map((c) => new ContactProxy(c)));
    }
    onPending() {
        return super
            .fromCallback('onPending')
            .pipe(map((c) => new ContactProxy(c)));
    }
    onConnecting() {
        return super
            .fromCallback('onConnecting')
            .pipe(map((c) => new ContactProxy(c)));
    }
    onAccepted() {
        return super
            .fromCallback('onAccepted')
            .pipe(map((c) => new ContactProxy(c)));
    }
    onMissed() {
        return super
            .fromCallback('onMissed')
            .pipe(map((c) => new ContactProxy(c)));
    }
    onEnded() {
        return super
            .fromCallback('onEnded')
            .pipe(map((c) => new ContactProxy(c)));
    }
    onDestroy() {
        return super
            .fromCallback('onDestroy')
            .pipe(map((c) => new ContactProxy(c)));
    }
    onACW() {
        return super
            .fromCallback('onACW')
            .pipe(map((c) => new ContactProxy(c)));
    }
    onConnected() {
        return super
            .fromCallback('onConnected')
            .pipe(map((c) => new ContactProxy(c)));
    }
    onError() {
        return super
            .fromCallback('onError')
            .pipe(map((c) => new ContactProxy(c)));
    }
    getContactId() {
        return super.fromData('getContactId');
    }
    getOriginalContactId() {
        return super.fromData('getOriginalContactId');
    }
    getState() {
        return super.fromData('getState');
    }
    getName() {
        return super.fromData('getName');
    }
    getDescription() {
        return super.fromData('getDescription');
    }
    getStateDuration() {
        return super.fromData('getStateDuration');
    }
    getQueue() {
        return super.fromData('getQueue');
    }
    getQueueTimestamp() {
        return super.fromData('getQueueTimestamp');
    }
    getConnections() {
        return super
            .fromData('getConnections')
            .pipe(map((cos) => cos.map((co) => new ConnectionProxy(co))));
    }
    getInitialConnection() {
        return super
            .fromData('getInitialConnection')
            .pipe(map((co) => new ConnectionProxy(co)));
    }
    getActiveInitialConnection() {
        return super
            .fromData('getActiveInitialConnection')
            .pipe(map((co) => new ConnectionProxy(co)));
    }
    getThirdPartyConnections() {
        return super
            .fromData('getThirdPartyConnections')
            .pipe(map((cos) => cos.map((co) => new ConnectionProxy(co))));
    }
    getSingleActiveThirdPartyConnection() {
        return super
            .fromData('getSingleActiveThirdPartyConnection')
            .pipe(map((co) => new ConnectionProxy(co)));
    }
    getAgentConnection() {
        return super
            .fromData('getAgentConnection')
            .pipe(map((co) => new ConnectionProxy(co)));
    }
    getAttributes() {
        return super.fromData('getAttributes');
    }
    isSoftphoneCall() {
        return super.fromData('isSoftphoneCall');
    }
    isInbound() {
        return super.fromData('isInbound');
    }
    isConnected() {
        return super.fromData('isConnected');
    }
    accept() {
        return super.fromResult('accept');
    }
    destroy() {
        return super.fromResult('destroy');
    }
    reject() {
        return super.fromResult('reject');
    }
    clear() {
        return super.fromResult('clear');
    }
    complete() {
        return super.fromResult('complete');
    }
    addConnection(endpoint) {
        return super.fromResult('addConnection', [endpoint]);
    }
    toggleActiveConnections() {
        return super.fromResult('toggleActiveConnections');
    }
    conferenceConnections() {
        return super.fromResult('conferenceConnections');
    }
}

class AgentProxy extends ConnectProxy {
    constructor(agent) {
        super(of(agent));
    }
    onRefresh() {
        return super
            .fromCallback('onRefresh')
            .pipe(map((a) => new AgentProxy(a)));
    }
    onStateChange() {
        return super.fromCallback('onStateChange');
    }
    onRoutable() {
        return super
            .fromCallback('onRoutable')
            .pipe(map((a) => new AgentProxy(a)));
    }
    onNotRoutable() {
        return super
            .fromCallback('onNotRoutable')
            .pipe(map((a) => new AgentProxy(a)));
    }
    onOffline() {
        return super
            .fromCallback('onOffline')
            .pipe(map((a) => new AgentProxy(a)));
    }
    onError() {
        return super
            .fromCallback('onError')
            .pipe(map((a) => new AgentProxy(a)));
    }
    onSoftphoneError() {
        return super.fromCallback('onSoftphoneError');
    }
    onAfterCallWork() {
        return super
            .fromCallback('onAfterCallWork')
            .pipe(map((a) => new AgentProxy(a)));
    }
    getState() {
        return super.fromData('getState');
    }
    getNextState() {
        return super.fromData('getNextState');
    }
    getStateDuration() {
        return super.fromData('getStateDuration');
    }
    getPermissions() {
        return super.fromData('getPermissions');
    }
    getContacts(contactTypeFilter) {
        const params = contactTypeFilter ? [contactTypeFilter] : [];
        return super
            .fromData('getContacts', params)
            .pipe(map((c) => c.map((cc) => new ContactProxy(cc))));
    }
    getConfiguration() {
        return super.fromData('getConfiguration');
    }
    getAgentStates() {
        return super.fromData('getAgentStates');
    }
    getRoutingProfile() {
        return super.fromData('getRoutingProfile');
    }
    getChannelConcurrency(channel) {
        const params = channel ? [] : [channel];
        return super.fromData('getChannelConcurrency', params);
    }
    getExtension() {
        return super.fromData('getExtension');
    }
    getName() {
        return super.fromData('getName');
    }
    getDialableCountries() {
        return super.fromData('getDialableCountries');
    }
    isSoftphoneEnabled() {
        return super.fromData('isSoftphoneEnabled');
    }
    setState(state, options) {
        const params = options ? [state] : [state, options];
        return super.setData('setState', params);
    }
    getAllQueueARNs() {
        return super.fromData('getAllQueueARNs');
    }
    getEndpoints(queueARNs) {
        return super.fromResult('getEndpoints', [
            queueARNs,
        ]);
    }
    mute() {
        return super.fromData('mute');
    }
    unmute() {
        return super.fromData('unmute');
    }
    setSpeakerDevice(deviceId) {
        return super.setData('setSpeakerDevice', [deviceId]);
    }
    setMicrophoneDevice(deviceId) {
        return super.setData('setMicrophoneDevice', [deviceId]);
    }
    setRingerDevice(deviceId) {
        return super.setData('setRingerDevice', [deviceId]);
    }
    setCameraDevice(deviceId) {
        return super.setData('setCameraDevice', [deviceId]);
    }
    setBackgroundBlur(isBackgroundBlurEnabled) {
        return super.setData('setBackgroundBlur', [isBackgroundBlurEnabled]);
    }
    onMuteToggle() {
        return super.fromCallback('onMuteToggle');
    }
    connect(endpoint, connectOptions) {
        const params = connectOptions ? [endpoint, connectOptions] : [endpoint];
        return super.setData('connect', params);
    }
    onSpeakerDeviceChanged() {
        return super.fromCallback('onSpeakerDeviceChanged');
    }
    onMicrophoneDeviceChanged() {
        return super.fromCallback('onMicrophoneDeviceChanged');
    }
    onRingerDeviceChanged() {
        return super.fromCallback('onRingerDeviceChanged');
    }
    onCameraDeviceChanged() {
        return super.fromCallback('onCameraDeviceChanged');
    }
    onBackgroundBlurChanged() {
        return super.fromCallback('onBackgroundBlurChanged');
    }
    onEnqueuedNextState() {
        return super
            .fromCallback('onEnqueuedNextState')
            .pipe(map((a) => new AgentProxy(a)));
    }
}

class TaskConnectionProxy extends ConnectionProxy {
    getMediaInfo() {
        return super.fromData('getMediaInfo');
    }
    getMediaType() {
        return super.fromData('getMediaType');
    }
    getMediaController() {
        return super.fromResult('getMediaController');
    }
}

class ChatConnectionProxy extends ConnectionProxy {
    getMediaInfo() {
        return super.fromData('getMediaInfo');
    }
    getMediaType() {
        return super.fromData('getMediaType');
    }
    getMediaController() {
        return super.fromResult('getMediaController');
    }
    getConnectionToken() {
        return super.fromResult('getConnectionToken');
    }
}

class VoiceConnectionProxy extends ConnectionProxy {
    getMediaInfo() {
        return super.fromData('getMediaInfo');
    }
    getMediaType() {
        return super.fromData('getMediaType');
    }
    getMediaController() {
        return super.fromResult('getMediaController');
    }
    getVoiceIdSpeakerId() {
        return super.fromResult('getVoiceIdSpeakerId');
    }
    getVoiceIdSpeakerStatus() {
        return super.fromResult('getVoiceIdSpeakerStatus');
    }
    optOutVoiceIdSpeaker() {
        return super.fromResult('optOutVoiceIdSpeaker');
    }
    evaluateSpeakerWithVoiceId(startNewSession) {
        return super.fromResult('evaluateSpeakerWithVoiceId', [startNewSession]);
    }
    enrollSpeakerInVoiceId() {
        return super.fromData('enrollSpeakerInVoiceId');
    }
    updateVoiceIdSpeakerId(speakerId) {
        return super.fromResult('updateVoiceIdSpeakerId', [speakerId]);
    }
    deleteVoiceIdSpeakerId() {
        return super.fromResult('deleteVoiceIdSpeakerId');
    }
    getQuickConnectName() {
        return super.fromData('getQuickConnectName');
    }
    isMute() {
        return super.fromData('isMute');
    }
    muteParticipant() {
        return super.fromData('muteParticipant');
    }
    unmuteParticipant() {
        return super.fromData('unmuteParticipant');
    }
    canSendVideo() {
        return super.fromData('canSendVideo');
    }
    getCapabilities() {
        return super.fromData('getCapabilities');
    }
    getVideoConnectionInfo() {
        return super.fromData('getVideoConnectionInfo');
    }
    isSilentMonitor() {
        return super.fromData('isSilentMonitor');
    }
    isBarge() {
        return super.fromData('isBarge');
    }
    isSilentMonitorEnabled() {
        return super.fromData('isSilentMonitorEnabled');
    }
    isBargeEnabled() {
        return super.fromData('isBargeEnabled');
    }
    getMonitorCapabilities() {
        return super.fromResult('getMonitorCapabilities');
    }
    getMonitorStatus() {
        return super.fromResult('getMonitorStatus');
    }
    isForcedMute() {
        return super.fromData('isForcedMute');
    }
}

class StorageManager {
    static { this.storageSubscriber = null; }
    static init(storageEmitter) {
        if (!this.storageSubscriber) {
            // @ts-expect-error: arrow function type not supported
            this.storageSubscriber = connect.storageAccess.onRequest({
                onInit: (data) => {
                    storageEmitter.emit(data);
                },
                onRequest: (data) => {
                    storageEmitter.emit(data);
                },
                onDeny: (data) => {
                    storageEmitter.emit(data);
                },
                onGrant: (data) => {
                    storageEmitter.emit(data);
                },
            });
        }
    }
    static destroy() {
        if (this.storageSubscriber) {
            this.storageSubscriber.unsubscribe();
            this.storageSubscriber = null;
        }
    }
}

var StorageAccessEvent;
(function (StorageAccessEvent) {
    StorageAccessEvent["INIT"] = "storageAccess::init";
    StorageAccessEvent["GRANTED"] = "storageAccess::granted";
    StorageAccessEvent["DENIED"] = "storageAccess::denied";
    StorageAccessEvent["REQUEST"] = "storageAccess::request";
})(StorageAccessEvent || (StorageAccessEvent = {}));

class StreamsService {
    constructor() {
        this._agent$ = new BehaviorSubject(null);
        this._contact$ = new BehaviorSubject(null);
    }
    /**
     * Returns a reference to an contact stream
     * @returns Observable with the lastest ContactProxy
     */
    get contact$() {
        return this._contact$.asObservable().pipe(filter$1((contact) => !!contact));
    }
    /**
     * Returns a reference to an agent stream
     * @returns Observable with the lastest AgentProxy
     */
    get agent$() {
        return this._agent$.asObservable().pipe(filter$1((agent) => !!agent));
    }
    /**
     * Returns an endpoint for a give phoneNumber
     * @param phoneNumber - phoneNumber in E.164 format
     * @returns connect.Endpoint
     */
    getEndpointByPhoneNumber(phoneNumber) {
        return of(connect.Endpoint.byPhoneNumber(phoneNumber));
    }
    /**
     * If the ccp was initialized get a reference to the eventBus, otherwise this is undefined.
     * @returns connect.EventBus
     */
    getEventBus() {
        // @ts-expect-error: declaration file missing types
        return connect.core.getEventBus();
    }
    logout() {
        if (!this._config) {
            return of(false);
        }
        const instanceUrl = new URL(this._config.ccpConfig.ccpUrl).origin;
        return fromFetch(`${instanceUrl}/connect/logout`, {
            credentials: 'include',
            mode: 'no-cors',
        }).pipe(switchMap(() => {
            connect.core.terminate();
            return of(true);
        }), catchError((err) => {
            return of({ error: true, message: err.message });
        }));
    }
    /**
     * Update contact event stream with the latest contactProxy.
     * @internal
     */
    _updateContact(contact) {
        this._contact$.next(contact);
    }
    /**
     * Update agent event stream with the latest agentProxy.
     * @internal
     */
    _updateAgent(agent) {
        this._agent$.next(agent);
    }
    _setConfig(config) {
        this._config = config;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.4", ngImport: i0, type: StreamsService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.4", ngImport: i0, type: StreamsService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.4", ngImport: i0, type: StreamsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class StreamsComponent {
    constructor(streams) {
        this.streams = streams;
        this.accessDenied = new EventEmitter();
        this.authFailed = new EventEmitter();
        this.authSuccess = new EventEmitter();
        this.retriesExhausted = new EventEmitter();
        this.viewContact = new EventEmitter();
        this.sessionInit = new EventEmitter();
        this.initialized = new EventEmitter();
        this.storageAccess = new EventEmitter();
    }
    ngAfterViewInit() {
        this.initCcp();
    }
    initCcp() {
        const ccpContainer = this.ccpContainer.nativeElement;
        if (!ccpContainer) {
            throw new Error('Container element missing!');
        }
        connect.core.initCCP(ccpContainer, this.config.ccpConfig);
        this.streams._setConfig(this.config);
        if (this.config.notificationConfig &&
            this.config.notificationConfig.enableCustomNotification) {
            NotificationManager.requestPermission();
            NotificationManager.showNotification(this.config.notificationConfig);
        }
        connect.agent((agent) => {
            this.streams._updateAgent(new AgentProxy(agent));
        });
        connect.contact((contact) => {
            this.streams._updateContact(new ContactProxy(contact));
        });
        connect.core.onAccessDenied(() => this.accessDenied.emit());
        connect.core.onAuthFail(() => this.authFailed.emit());
        connect.core.onAuthorizeRetriesExhausted(() => this.retriesExhausted.emit());
        connect.core.onAuthorizeSuccess(() => this.authSuccess.emit());
        connect.core.onInitialized(() => this.initialized.emit());
        connect.core.onViewContact((event) => this.viewContact.emit(event));
        // @ts-expect-error: declaration file missing types
        connect.core.onSoftphoneSessionInit((event) => {
            // @ts-expect-error: declaration file missing types
            const softphoneManager = connect.core.getSoftphoneManager();
            if (softphoneManager) {
                const session = softphoneManager.getSession(event.connectionId);
                this.sessionInit.emit(session);
            }
        });
        StorageManager.init(this.storageAccess);
    }
    changeViewContact(contactId) {
        connect.core.viewContact(contactId);
    }
    terminate() {
        this.destroy();
    }
    destroy() {
        if (this.ccpContainer) {
            const ccpIFrame = this.ccpContainer.nativeElement.firstElementChild;
            if (ccpIFrame) {
                this.ccpContainer.nativeElement.removeChild(ccpIFrame);
            }
        }
        StorageManager.destroy();
        connect.core.terminate();
    }
    ngOnDestroy() {
        this.destroy();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.4", ngImport: i0, type: StreamsComponent, deps: [{ token: StreamsService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.4", type: StreamsComponent, selector: "trccp-streams", inputs: { config: "config" }, outputs: { accessDenied: "accessDenied", authFailed: "authFailed", authSuccess: "authSuccess", retriesExhausted: "retriesExhausted", viewContact: "viewContact", sessionInit: "sessionInit", initialized: "initialized", storageAccess: "storageAccess" }, viewQueries: [{ propertyName: "ccpContainer", first: true, predicate: ["ccpContainer"], descendants: true }], ngImport: i0, template: `
    <div
      #ccpContainer
      [class.hidden]="this.config?.hideCCP ?? false"
      [style.height.px]="this.config?.pxHeight ?? 460"
      [style.width.px]="this.config?.pxWidth ?? 320"
    ></div>
  `, isInline: true, styles: ["::ng-deep iframe{border:none}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.4", ngImport: i0, type: StreamsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'trccp-streams', template: `
    <div
      #ccpContainer
      [class.hidden]="this.config?.hideCCP ?? false"
      [style.height.px]="this.config?.pxHeight ?? 460"
      [style.width.px]="this.config?.pxWidth ?? 320"
    ></div>
  `, styles: ["::ng-deep iframe{border:none}\n"] }]
        }], ctorParameters: () => [{ type: StreamsService }], propDecorators: { ccpContainer: [{
                type: ViewChild,
                args: ['ccpContainer']
            }], config: [{
                type: Input
            }], accessDenied: [{
                type: Output
            }], authFailed: [{
                type: Output
            }], authSuccess: [{
                type: Output
            }], retriesExhausted: [{
                type: Output
            }], viewContact: [{
                type: Output
            }], sessionInit: [{
                type: Output
            }], initialized: [{
                type: Output
            }], storageAccess: [{
                type: Output
            }] } });

class StreamsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.4", ngImport: i0, type: StreamsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.4", ngImport: i0, type: StreamsModule, declarations: [StreamsComponent], exports: [StreamsComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.4", ngImport: i0, type: StreamsModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.4", ngImport: i0, type: StreamsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        StreamsComponent
                    ],
                    imports: [],
                    exports: [StreamsComponent]
                }]
        }] });

/*
 * Public API Surface of streams
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AgentProxy, ChatConnectionProxy, ConnectionProxy, ContactProxy, NotificationManager, StorageAccessEvent, StorageManager, StreamsComponent, StreamsModule, StreamsService, TaskConnectionProxy, VoiceConnectionProxy };
